import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';
import { fetchMemberDemoGraphicInfo } from './memberDetail.slice';
import { fetchProvider, setIsLoading, setShowMessage } from './shared.slice';
import { EncounterList } from '../Models/Encounters/EncounterList';
import { MemberEncounterDraft } from '../Models/Member/MemberEncounterDraft.model';
import { DiagnosisDraft } from '../Models/DocumentEngagements/DiagnosisDraft.model';
import { convertEncountersToList, getICD10DiagnosisByCode, setDefaultDiagnosis } from './encounter.slice';
import { setClaim, setClaimNumber, setShowEditResubmitModal } from './claimResubmission.slice';
import { MemberDefaultDiagnosis } from '../Models/DocumentEngagements/MemberDefaultDiagnosis.model';
import { copyObject } from '../utils/common';
import CPTCodeGroup from '../Models/Shared/CPTCodeGroup';
import { formatDate } from '../utils/dateHelpers';
import { toZonedTime } from 'date-fns-tz';
import { MessageAlert } from '../Models/Shared/MessageAlert.model';
import ICPTModifier from '../Models/Shared/CPTModifier.model';

const procedureSignOffEncounterSlice = createSlice({
    name: 'procedureSignOffEncounter',
    initialState: {
        dateOfService: new Date(),
        deletedEncounterIds: [],
        draftEncounterList: new EncounterList(),
        encounterChange: false,
        encounterDate: '',
        encounterStatus: '',
        showProcedureSignOffModal: false,
        error: new MessageAlert(),
        placeOfServiceId: '',
    },
    reducers: {
        _setDateOfService(state, action) {
            state.dateOfService = action.payload;
        },
        _setDeletedEncounterIds(state, action) {
            state.deletedEncounterIds = action.payload;
        },
        _setDraftEncounterList(state, action) {
            state.draftEncounterList = action.payload;
        },
        _setEncounterChange(state, action) {
            state.encounterChange = action.payload;
        },
        _setEncounterDate(state, action) {
            state.encounterDate = action.payload;
        },
        _setEncounterStatus(state, action) {
            state.encounterStatus = action.payload;
        },
        _setShowProcedureSignOffModal(state, action) {
            state.showProcedureSignOffModal = action.payload;
        },
        _setError(state, action) {
            state.error = action.payload;
        },
        _setPlaceOfServiceId(state, action) {
            state.placeOfServiceId = action.payload;
        },
    },
});

const {
    _setDateOfService,
    _setDeletedEncounterIds,
    _setDraftEncounterList,
    _setEncounterChange,
    _setEncounterDate,
    _setEncounterStatus,
    _setShowProcedureSignOffModal,
    _setError,
    _setPlaceOfServiceId,
} = procedureSignOffEncounterSlice.actions;

export const setClaimError =
    (error?: MessageAlert): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setError(error));
    };

export const setDateOfService =
    (dateOfService: Date): AppThunk =>
    (dispatch) => {
        dispatch(_setDateOfService(dateOfService));
    };

export const setPlaceOfServiceId =
    (placeOfServiceId: string): AppThunk =>
    (dispatch) => {
        dispatch(_setPlaceOfServiceId(placeOfServiceId));
    };

export const fetchModifiersForEncounter =
    (cptCodeId: string, placeOfEncounterValue: number, onFetched?: (modifiers: ICPTModifier[]) => void): AppThunk =>
    async () => {
        const response = await axios.get(`${API_URL}/encounter/getmodifiersforencounter?cptCodeId=${cptCodeId}&placeOfEncounterValue=${placeOfEncounterValue}`);
        const modifiers = response.data as ICPTModifier[];
        if (onFetched) onFetched(modifiers);
    };

export const fetchEncountersByDate =
    (placeOfService: number, encounterStatus: string, memberId: string, providerId: string, encounterDate: string, callback: Function = null, engagementId?: string): AppThunk =>
    async (dispatch) => {
        if (encounterDate.indexOf(':') > -1) {
            encounterDate = encounterDate.split(' ')[0];
        }

        try {
            const response = await axios.get(`${API_URL}/encounter/getmemberencountersbydate`, {
                params: { placeOfService, memberId, providerId, date: encounterDate, encounterStatus, engagementId },
            });
            if (response.data.length === 0) {
                dispatch(setDraftEncounterList(new EncounterList()));
                dispatch(setShowMessage(true, 'No editable encounters were found. The assessment may have been reopened as a draft.', 'error'));
                return;
            }

            dispatch(fetchProvider(response.data[0].CreatedBy.Id));
            dispatch(setDateOfService(response.data[0].DateOfService));
            dispatch(setPlaceOfServiceId(response.data[0].PlaceOfEncounterId));
            let encounterList = await convertEncountersToEncounterList(response.data);
            encounterList = convertEncountersToList(encounterList);
            dispatch(setDraftEncounterList(encounterList));
            dispatch(setDeletedEncounterIds([]));
            dispatch(fetchMemberDemoGraphicInfo(memberId, callback));
        } catch (error) {
            dispatch(setShowMessage(true, error.response?.data?.error || 'Unable to load encounters. Please try again.', 'error'));
        }
    };

export const getICD10DiagnosisByCategoryUpdateList =
    (categoryId: string, encounterIndex: number, diagnosisIndex: number, bindResponsePropName: string, isDefaultDiagnosis = false): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/member/geticd10diagnosisbycategory`, { params: { categoryId } });
        if (isDefaultDiagnosis) {
            let memberDefaultDiagnosis = copyObject(getState().encounter.memberDefaultDiagnosis) as MemberDefaultDiagnosis;
            memberDefaultDiagnosis.DefaultDiagnosis[bindResponsePropName] = response.data;

            dispatch(setDefaultDiagnosis(memberDefaultDiagnosis));
        } else {
            let encounterList = copyObject(getState().procedureSignOffEncounter.draftEncounterList);
            encounterList.Encounters[encounterIndex].DiagnosisList[diagnosisIndex][bindResponsePropName] = response.data;

            dispatch(_setDraftEncounterList(encounterList));
        }
    };

export const setDraftEncounterList =
    (encounterList: EncounterList): AppThunk =>
    (dispatch) => {
        dispatch(_setDraftEncounterList(encounterList));
    };

export const setMemberEncounterDraft = async (draftEncounter: MemberEncounterDraft, encounter: any) => {
    draftEncounter.Id = encounter.Id;
    draftEncounter.Units = encounter.Units;
    draftEncounter.CptCodeId = encounter.CPTcode?.Id;
    draftEncounter.CptCode = encounter.CPTcode?.Code;
    draftEncounter.CategoryGroupId = encounter.CategoryGroupId;
    draftEncounter.MemberId = encounter.MemberId;
    draftEncounter.EngagementId = encounter.EngagementId;
    draftEncounter.PlaceOfEncounterValue = encounter.PlaceOfEncounterValue;
    draftEncounter.PlaceOfEncounterId = encounter.PlaceOfEncounterId;
    draftEncounter.ServiceDate = encounter.DateOfService;
    draftEncounter.TraceId = encounter.TraceId;
    draftEncounter.Modifier = encounter.Modifier;
    draftEncounter.Modifier2 = encounter.Modifier2;
    draftEncounter.Modifier3 = encounter.Modifier3;
    draftEncounter.Modifier4 = encounter.Modifier4;
};

export const setMemberEncounterDraftDiagnosis = async (
    draftEncounter: MemberEncounterDraft,
    encounter: any,
    diagnosisName: string,
    diagnosisPointerName: string,
    diagnosisList: string
) => {
    if (encounter[diagnosisList] != null && encounter[diagnosisList].length > 0 && encounter[diagnosisList][0] != null) {
        draftEncounter[diagnosisName] = new DiagnosisDraft();
        draftEncounter[diagnosisName].Diagnosis = encounter[diagnosisList][0].Code + ' ' + encounter[diagnosisList][0].Name;
        draftEncounter[diagnosisName].DiagnosisPointer = encounter[diagnosisPointerName];

        if (encounter[diagnosisList].length >= 1) {
            draftEncounter[diagnosisName].FirstDiagnosis = encounter[diagnosisList][1];
            draftEncounter[diagnosisName].DiagnosisFirstList = await getICD10DiagnosisByCategory(encounter[diagnosisList][0].Code);
        }

        if (encounter[diagnosisList].length >= 2) {
            draftEncounter[diagnosisName].SecondDiagnosis = encounter[diagnosisList][2];
            draftEncounter[diagnosisName].DiagnosisSecondList = await getICD10DiagnosisByCategory(encounter[diagnosisList][1].Code);
        }

        if (encounter[diagnosisList].length >= 3) {
            draftEncounter[diagnosisName].ThirdDiagnosis = encounter[diagnosisList][3];
            draftEncounter[diagnosisName].DiagnosisThirdList = await getICD10DiagnosisByCategory(encounter[diagnosisList][2].Code);
        }

        if (encounter[diagnosisList].length >= 4) {
            draftEncounter[diagnosisName].FourthDiagnosis = encounter[diagnosisList][4];
            draftEncounter[diagnosisName].DiagnosisFourthList = await getICD10DiagnosisByCategory(encounter[diagnosisList][3].Code);
        }
    }
};

export const updateList = async (
    encounter: any,
    draft: MemberEncounterDraft,
    diagnosisDraft: DiagnosisDraft,
    diagnosisPointerName: string,
    diagnosisList: string
) => {
    encounter[diagnosisList] = [];
    if (diagnosisDraft && diagnosisDraft.DiagnosisPointer) {
        const diagnosis = await getICD10DiagnosisByCode(diagnosisDraft.Diagnosis.split(' ')[0]);
        encounter[diagnosisList].push(diagnosis);

        if (diagnosisDraft.FirstDiagnosis) {
            encounter[diagnosisList].push(diagnosisDraft.FirstDiagnosis);
        }
        if (diagnosisDraft.SecondDiagnosis) {
            encounter[diagnosisList].push(diagnosisDraft.SecondDiagnosis);
        }
        if (diagnosisDraft.ThirdDiagnosis) {
            encounter[diagnosisList].push(diagnosisDraft.ThirdDiagnosis);
        }
        if (diagnosisDraft.FourthDiagnosis) {
            encounter[diagnosisList].push(diagnosisDraft.FourthDiagnosis);
        }

        encounter[diagnosisPointerName] = diagnosisDraft.DiagnosisPointer;
    }
};

export const convertDraftToEncounters = async (
    encounterList: EncounterList,
    status: number,
    cptCodeCategories: CPTCodeGroup[],
    provider: any,
    selectedMember: any,
    dateOfService: Date,
    claimNumber: string | null
) => {
    let encounters = [];

    for (let draft of encounterList.Encounters) {
        const category = cptCodeCategories.find((c) => c.Id === draft.CategoryGroupId);
        // Look up the code the user selected by Id across every role in the category.
        // Role-first .find() can miss E&M codes that live on a later role (e.g. NP)
        // when an earlier role name has no spaces.
        const categoryCodes = (category?.CPTRoleCodes || []).flatMap((roleCode) => roleCode.Codes || []);
        let cptCode = categoryCodes.find((c) => c.Id === draft.CptCodeId && c.Code);
        if (!cptCode && draft.CptCode) {
            cptCode = categoryCodes.find((c) => c.Code === draft.CptCode && c.Code);
        }

        var encounter = {
            Id: draft.Id,
            MemberId: selectedMember.Id,
            EngagementId: draft.EngagementId,
            PlaceOfEncounterId: draft.PlaceOfEncounterId,
            PlaceOfEncounterValue: draft.PlaceOfEncounterValue,
            CategoryGroupId: draft.CategoryGroupId,
            Units: draft.Units,
            CPTcode: cptCode,
            ServiceDate: formatDate(toZonedTime(dateOfService, 'America/Phoenix'), 'MM/dd/yyyy HH:mm'),
            EncounterStatus: status,
            TraceId: claimNumber,
            Modifier: draft.Modifier,
            Modifier2: draft.Modifier2,
            Modifier3: draft.Modifier3,
            Modifier4: draft.Modifier4,
        };

        if (draft.DiagnosisList.length >= 1) {
            await updateList(encounter, draft, draft.DiagnosisList[0], 'PrimaryDiagnosisPointer', 'PrimaryDiagnosisList');
        }
        if (draft.DiagnosisList.length >= 2) {
            await updateList(encounter, draft, draft.DiagnosisList[1], 'SecondaryDiagnosisPointer', 'SecondaryDiagnosisList');
        }
        if (draft.DiagnosisList.length >= 3) {
            await updateList(encounter, draft, draft.DiagnosisList[2], 'TertiaryDiagnosisPointer', 'TertiaryDiagnosisList');
        }
        if (draft.DiagnosisList.length >= 4) {
            await updateList(encounter, draft, draft.DiagnosisList[3], 'QuaternaryDiagnosisPointer', 'QuaternaryDiagnosisList');
        }
        encounters.push(encounter);
    }
    return encounters;
};

export const updateEncounters =
    (
        encounterList: EncounterList,
        status: number,
        claimNumber: string | null,
        isResubmit: boolean,
        isClose: boolean = false,
        sendToWaystar: boolean = false
    ): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        try {
            let encounters = await convertDraftToEncounters(
                encounterList,
                status,
                getState().encounter.cptCodeCategories,
                getState().shared.currentProvider,
                getState().memberDetail.selectedMember,
                getState().procedureSignOffEncounter.dateOfService,
                claimNumber
            );

            if (encounters.some((e) => !e.CPTcode?.Code)) {
                dispatch(setIsLoading(false));
                dispatch(setShowMessage(true, 'Please add a primary CPT code to all encounters before submitting.', 'error'));
                return;
            }

            await axios.put(`${API_URL}/encounter/updateencounters`, {
                MemberEncounter: encounters,
                Claim: getState().claimResubmission.claim,
                Resubmit: isResubmit,
                CloseClaim: isClose,
                SendToWaystar: sendToWaystar,
                CreatedBy: getState().shared.currentProvider,
                DeletedIds: getState().procedureSignOffEncounter.deletedEncounterIds,
            });
            dispatch(setShowProcedureSignOffModal(false));
            dispatch(setClaim(null));
            dispatch(setClaimNumber(''));
            dispatch(setShowEditResubmitModal(false));
            dispatch(setDeletedEncounterIds([]));
            dispatch(setIsLoading(false));
            dispatch(setShowMessage(true, 'Successfully Updated', 'success'));
            dispatch(setEncounterChange(true));
        } catch (error) {
            const message = error.response?.data?.error || error.response?.data?.title || 'Unable to save the claim. Please try again.';
            dispatch(setShowMessage(true, message, 'error'));
            dispatch(setShowEditResubmitModal(false));
            dispatch(setIsLoading(false));
        }
    };

export const convertEncountersToEncounterList = async (encounters: any) => {
    let encounterList = new EncounterList();
    for (let encounter of encounters) {
        var draftEncounter = new MemberEncounterDraft();
        setMemberEncounterDraft(draftEncounter, encounter);

        await setMemberEncounterDraftDiagnosis(draftEncounter, encounter, 'PrimaryDiagnosis', 'PrimaryDiagnosisPointer', 'PrimaryDiagnosisList');
        await setMemberEncounterDraftDiagnosis(draftEncounter, encounter, 'SecondaryDiagnosis', 'SecondaryDiagnosisPointer', 'SecondaryDiagnosisList');
        await setMemberEncounterDraftDiagnosis(draftEncounter, encounter, 'TertiaryDiagnosis', 'TertiaryDiagnosisPointer', 'TertiaryDiagnosisList');
        await setMemberEncounterDraftDiagnosis(draftEncounter, encounter, 'QuaternaryDiagnosis', 'QuaternaryDiagnosisPointer', 'QuaternaryDiagnosisList');

        encounterList.Encounters.push(draftEncounter);
    }
    return encounterList;
};

export const hasDuplicateDiagnosisCodes = (encounterList: EncounterList) => {
    let hasDuplicate = false;
    let diagnosisHash = {};
    encounterList.Encounters.forEach((encounter) => {
        encounter.DiagnosisList.forEach((diagnosis) => {
            let duplicateCode =
                diagnosisHash[
                    encounter.CptCode +
                        diagnosis.DiagnosisPointer?.Code +
                        diagnosis.FirstDiagnosis?.Code +
                        diagnosis.SecondDiagnosis?.Code +
                        diagnosis.ThirdDiagnosis?.Code +
                        diagnosis.FourthDiagnosis?.Code
                ];
            if (duplicateCode) {
                console.log('Duplicate found', JSON.stringify(encounter));
                hasDuplicate = true;
            } else {
                diagnosisHash[
                    encounter.CptCode +
                        diagnosis.DiagnosisPointer?.Code +
                        diagnosis.FirstDiagnosis?.Code +
                        diagnosis.SecondDiagnosis?.Code +
                        diagnosis.ThirdDiagnosis?.Code +
                        diagnosis.FourthDiagnosis?.Code
                ] = true;
            }
        });
    });
    if (hasDuplicate) {
        console.log('Duplicate diagnosis codes found', JSON.stringify(encounterList.Encounters));
    }
    return hasDuplicate;
};

export const getICD10DiagnosisByCategory = async (categoryId: string) => {
    const response = await axios.get(`${API_URL}/member/geticd10diagnosisbycategory`, { params: { categoryId } });
    return response.data;
};

export const setEncounterDate =
    (encounterDate: string): AppThunk =>
    (dispatch) => {
        dispatch(_setEncounterDate(encounterDate));
    };

export const setEncounterStatus =
    (encounterStatus: string): AppThunk =>
    (dispatch) => {
        dispatch(_setEncounterStatus(encounterStatus));
    };

export const setDeletedEncounterIds =
    (deletedIds: string[]): AppThunk =>
    (dispatch) => {
        dispatch(_setDeletedEncounterIds(deletedIds));
    };

export const setShowProcedureSignOffModal =
    (show: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setShowProcedureSignOffModal(show));
    };

export const setEncounterChange =
    (show: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_setEncounterChange(show));
    };

export const getTop = (list: EncounterList) => {
    if (list && list.Encounters.length >= 1) {
        let value = list?.Encounters.length * 335;
        if (list?.Encounters.length > 8) {
            value = list?.Encounters.length * 300;
        }
        return value > 600 ? value : 600;
    } else {
        return 500;
    }
};

export const prepareICD10Hierarchy =
    (code: string, index: number, dxIndex: number): AppThunk =>
    async (dispatch, getState) => {
        axios.get(`${API_URL}/encounter/prepareicd10hierarchy`, { params: { code } }).then((response) => {
            let data = copyObject(getState().procedureSignOffEncounter.draftEncounterList);
            data.Encounters[index].DiagnosisList[dxIndex] = response.data;
            dispatch(setDraftEncounterList(data));
        });
    };

export default procedureSignOffEncounterSlice.reducer;
